import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { AlertTriangle, Loader2, Sparkles, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { suggestServiceForSymptoms, type SymptomAdviceResult } from "@/lib/symptom-advisor.functions";
import { useBusinessConfig } from "@/lib/use-business-config";

const SERVICE_LABELS: Record<string, string> = {
  mot: "MOT Test",
  "interim-service": "Interim Service",
  "full-service": "Full Service",
  "major-service": "Major Service",
  diagnostics: "Diagnostics / Warning Light",
  brakes: "Brake Repair",
  "clutch-gearbox": "Clutch / Gearbox",
  bodywork: "Bodywork / Paint",
  other: "General inspection",
};

type Advice = Extract<SymptomAdviceResult, { ok: true }>["advice"];

export function SymptomAdvisor() {
  const biz = useBusinessConfig();
  const suggest = useServerFn(suggestServiceForSymptoms);
  const [symptoms, setSymptoms] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advice, setAdvice] = useState<Advice | null>(null);
  const [enquiry, setEnquiry] = useState("");

  const tooShort = symptoms.trim().length < 10;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (tooShort || loading) return;
    setLoading(true);
    setError(null);
    setAdvice(null);
    try {
      const res = await suggest({ data: { symptoms, vehicle: vehicle || undefined } });
      if (res.ok) {
        setAdvice(res.advice);
        setEnquiry(res.advice.enquiry);
      } else setError(res.error);
    } catch {
      setError("We couldn't get a suggestion just now. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm">
      <form onSubmit={onSubmit} className="space-y-4" aria-label="Describe your vehicle symptoms">
        <div>
          <Label htmlFor="sa-vehicle" className="text-sm">Vehicle (optional)</Label>
          <Input id="sa-vehicle" value={vehicle} maxLength={120} onChange={(e) => setVehicle(e.target.value)} placeholder="e.g. 2016 Ford Focus" />
        </div>
        <div>
          <Label htmlFor="sa-symptoms" className="text-sm">What's happening?</Label>
          <Textarea
            id="sa-symptoms"
            value={symptoms}
            maxLength={1200}
            rows={4}
            onChange={(e) => setSymptoms(e.target.value)}
            placeholder="e.g. Grinding noise when braking, started last week, worse when cold…"
          />
          <p className="mt-1 text-xs text-muted-foreground">Please don't include your name or phone number here.</p>
        </div>
        <Button type="submit" disabled={tooShort || loading} className="w-full sm:w-auto">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {loading ? "Thinking…" : "Suggest a service"}
        </Button>
      </form>

      {error && (
        <p role="alert" className="mt-5 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-foreground">
          {error} <a href={biz.phoneHref} className="font-semibold underline">Call {biz.phone}</a>
        </p>
      )}

      {advice && (
        <div className="mt-6 space-y-4" aria-live="polite" data-testid="symptom-advice">
          {advice.safetyWarning && (
            <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-foreground">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
              <span>{advice.safetyWarning} If in doubt, <a href={biz.phoneHref} className="font-semibold underline">call {biz.phone}</a>.</span>
            </div>
          )}
          <div className="rounded-lg bg-muted p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Wrench className="h-4 w-4 text-primary" /> Suggested: {SERVICE_LABELS[advice.service] ?? advice.service}
              <span className="ml-auto rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">{advice.urgency}</span>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">{advice.explanation}</p>
          </div>
          <div>
            <Label htmlFor="sa-enquiry" className="text-sm">Your booking enquiry (edit if needed)</Label>
            <Textarea id="sa-enquiry" value={enquiry} maxLength={500} rows={3} onChange={(e) => setEnquiry(e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/book" search={{ service: advice.service, enquiry: enquiry.slice(0, 500), context: "symptom-advisor" }}>
                Continue to booking
              </Link>
            </Button>
            <Button asChild variant="outline">
              <a href={biz.phoneHref}>Call {biz.phone}</a>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            AI-powered suggestion only — a mechanic confirms the cause after inspection. No work without your approval.
          </p>
        </div>
      )}
    </div>
  );
}
